<template>
  <!-- TV-Guide v2. Farben ausschliesslich ueber CSS-Variablen, die hier per
       :style gesetzt werden - damit laesst sich das Theme spaeter aus dem
       Customizer speisen, ohne das Stylesheet anzufassen (siehe themeStyles). -->
  <div
    class="epg2"
    :class="{
      'epg2--preview': previewActive,
      'epg2--zone-list': showInfo && infoZone === 'list',
    }"
    :style="themeStyles"
    ref="root"
  >
    <div class="epg2__head">
      <!-- Zurueck-Knopf oben links wie auf den uebrigen Seiten (Vod, Cast,
           MediaPlayer). Er ist vor allem SICHTBARKEIT: dass ZURUECK aus dem
           Programm herausfuehrt, stand bisher nur im Hilfstext ganz unten.
           Erreichbar ist er mit HOCH aus der obersten Kanalzeile - eine eigene
           Richtung ist im Raster nicht frei, HOCH/RUNTER blaettert Sender und
           LINKS/RECHTS die Sendungen. -->
      <BackButton
        class="epg2__back"
        :is-active="isBackFocused"
        @click="leave"
      >
        {{ getBack }}
      </BackButton>
      <div class="epg2__title">{{ getTitle }}</div>
      <div class="epg2__clock">{{ clockLabel }}</div>
      <div class="epg2__status">
        <span v-if="isLoading">{{ getLoading }} {{ progress.loaded }}/{{ progress.total }}</span>
        <span v-else-if="error" class="epg2__error">{{ error }}</span>
        <span v-else>{{ viewRangeLabel }}</span>
      </div>
    </div>

    <!-- Zeitachse. Nur die sichtbaren Slots, nicht alle 96 der 48h. -->
    <div class="epg2__axis">
      <div class="epg2__axis-spacer">{{ dayLabel }}</div>
      <div class="epg2__axis-lane">
        <div
          v-for="slot in slots"
          :key="slot.at"
          class="epg2__axis-slot"
          :style="{ left: slot.offsetPercent + '%', width: slot.widthPercent + '%' }"
        >{{ slot.label }}</div>
        <div
          v-if="nowPercent !== null"
          class="epg2__now"
          :style="{ left: nowPercent + '%' }"
        ></div>
      </div>
    </div>

    <!-- Kanalzeilen. Es werden nur die sichtbaren Zeilen gerendert; bei 60+
         Sendern waere eine vollstaendige Tabelle auf der TV-CPU zu langsam. -->
    <div class="epg2__rows">
      <div
        v-for="row in visibleRows"
        :key="row.channel._id || row.channel.id || row.channel.position"
        class="epg2__row"
        :class="{ 'epg2__row--focus': row.index === focusChannelIndex }"
      >
        <div class="epg2__channel">
          <div class="epg2__channel-pos">{{ row.channel.position }}</div>
          <img
            class="epg2__channel-logo"
            :src="row.logo"
            :alt="row.channel.name"
            @error="onLogoError"
          />
          <div class="epg2__channel-name">{{ row.channel.name }}</div>
        </div>
        <div class="epg2__lane">
          <div
            v-for="item in row.programs"
            :key="item.program.id"
            class="epg2__program"
            :class="{
              'epg2__program--focus': row.index === focusChannelIndex && item.index === focusProgramIndex,
              'epg2__program--running': item.progress !== null,
            }"
            :style="{ left: item.layout.leftPercent + '%', width: item.layout.widthPercent + '%' }"
          >
            <div class="epg2__program-title">{{ item.program.title }}</div>
            <div class="epg2__program-time">{{ item.program.start }}</div>
            <!-- Schmaler Balken: wie weit die laufende Sendung fortgeschritten ist -->
            <div
              v-if="item.progress !== null"
              class="epg2__program-progress"
              :style="{ width: item.progress + '%' }"
            ></div>
          </div>
          <div v-if="!row.programs.length" class="epg2__empty">
            {{ row.status === 'loading' ? getLoading : getNoData }}
          </div>
          <div
            v-if="nowPercent !== null"
            class="epg2__now epg2__now--row"
            :style="{ left: nowPercent + '%' }"
          ></div>
        </div>
      </div>
      <div
        v-if="rowFillerGrow > 0"
        class="epg2__rows-filler"
        :style="{ flexGrow: rowFillerGrow }"
      ></div>
    </div>

    <div class="epg2__hint">{{ getHint }}</div>

    <!-- Erweiterte Info nach dem ersten OK. Zweites OK springt zum Sender. -->
    <div class="epg2__info" v-if="showInfo && focusProgram">
      <div
        class="epg2__info-box"
        :class="{ 'epg2__info-box--active': infoZone === 'text' }"
      >
        <div class="epg2__info-channel">
          <img class="epg2__channel-logo" :src="focusLogo" :alt="focusChannelName" @error="onLogoError" />
          <span>{{ focusChannelName }}</span>
        </div>
        <div class="epg2__info-title">{{ focusProgram.title }}</div>
        <div class="epg2__info-time">
          {{ focusProgram.start }} – {{ focusProgram.end }}
          <span v-if="focusProgress !== null" class="epg2__info-live">{{ getRunning }}</span>
        </div>
        <div v-if="focusProgress !== null" class="epg2__info-progress">
          <div class="epg2__info-progress-bar" :style="{ width: focusProgress + '%' }"></div>
        </div>
        <!-- Scrollbereich: laengere Beschreibungen werden mit HOCH/RUNTER
             gescrollt, damit der Hilfstext darunter immer sichtbar bleibt. -->
        <div class="epg2__info-text" ref="infoText">{{ focusDescription }}</div>
        <div class="epg2__info-more" v-if="infoScrollable">
          <span v-if="!infoAtStart">▲</span><span v-if="!infoAtEnd">▼</span>
        </div>
        <div class="epg2__info-hint">{{ getInfoHint }}</div>
      </div>

      <!-- Flaeche der Live-Vorschau.
           WICHTIG: bewusst NEBEN dem Info-Kasten und ohne Hintergrundfarbe. Das
           Videobild liegt auf einer eigenen Hardware-Ebene HINTER dem HTML -
           jedes deckende HTML-Pixel an dieser Stelle wuerde es verdecken. Nur
           ein dünner Rahmen, damit der Ausschnitt erkennbar bleibt.
           Die Position muss mit PREVIEW_RECT uebereinstimmen. -->
      <div class="epg2__preview" v-if="livePreviewEnabled"></div>

      <!-- Was im Vorschaubild tatsaechlich laeuft. Immer sichtbar, damit die
           Angabe ihren festen Platz hat - auch wenn die Beschreibung oben
           dieselbe Sendung zeigt. Weicht sie ab (in der Zeitleiste wurde eine
           spaetere Sendung gewaehlt), wird das zusaetzlich markiert. -->
      <div class="epg2__preview-now" v-if="livePreviewEnabled">
        <div class="epg2__preview-now-label">{{ getNowOnAir }}</div>
        <template v-if="nowProgram">
          <div class="epg2__preview-now-title">
            {{ nowProgram.title }}<span
              v-if="nowProgramDiffers"
              class="epg2__preview-now-flag"
            > · {{ getRunning }}</span>
          </div>
          <div class="epg2__preview-now-time">{{ nowProgram.start }} – {{ nowProgram.end }}</div>
          <div class="epg2__preview-now-bar" v-if="nowProgramProgress !== null">
            <div
              class="epg2__preview-now-bar-fill"
              :style="{ width: nowProgramProgress + '%' }"
            ></div>
          </div>
        </template>
        <div class="epg2__preview-now-time" v-else>{{ getNoData }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { keyHandler, KEYS } from '@/mixins/keyHandler';
import { NAV_TYPE } from '@/store/modules/navigation';
import BackButton from '@/components/BackButton.vue';
import TV from '@/helpers/TV';
import {
  MINUTE,
  VIEW_MINUTES,
  timeSlots,
  viewWindow,
  clampViewStart,
  viewStartFor,
  programLayout,
  programProgress,
  isRunning,
  nowMarkerPercent,
  indexOfCurrentProgram,
  epgLogoUrl,
  formatDayShort,
  formatTime,
  EPG_LOGO_FALLBACK,
} from '@/helpers/EpgGuide2';

// Gleichzeitig dargestellte Kanalzeilen.
const ROWS = 7;

// Takt der "jetzt"-Markierung und der Fortschrittsbalken. Kostet keine API-Last,
// nur eine Neuberechnung der sichtbaren Zeilen - deshalb ruhig kurz.
const NOW_TICK_MS = 30 * 1000;

// Takt, in dem der Store pruefen darf, ob Daten oder Zeitfenster erneuert werden
// muessen. Der Store entscheidet dann selbst, ob wirklich geladen wird.
const REFRESH_TICK_MS = 5 * 60 * 1000;

// Live-Vorschau des fokussierten Senders im Info-Bereich.
//
// Setzt Kanal und Videoflaeche (LG hcap.video.setVideoSize, Tizen tvwindow). Auf
// LG ist die Videogroesse GLOBALER Geraetezustand: sie bleibt bestehen, nachdem
// die Seite verlassen wurde. Deshalb wird sie an jedem Ausgang zurueckgesetzt -
// Info schliessen, Sprung zum Sender, Verlassen der Seite, Wechsel in den
// Hintergrund (siehe stopPreview/beforeDestroy).
const LIVE_PREVIEW_ENABLED = true;

// Rechteck der Vorschau in Prozent der Bildflaeche.
// MUSS deckungsgleich mit .epg2__preview im Stylesheet sein (left/top/width/
// height dort in vw/vh) - das Videobild liegt auf einer eigenen Ebene und weiss
// nichts vom HTML-Layout. Weicht beides voneinander ab, sitzt das Bild neben
// seinem Rahmen.
const PREVIEW_RECT = { x: 55, y: 30, width: 35, height: 35 };

// Verzoegerung, bevor tatsaechlich getunt wird. Ohne das wuerde jeder
// Tastendruck im Info-Fenster einen Kanalwechsel ausloesen - unnoetige Last und
// ein sichtbares Flackern. Kurz genug, dass es beim Durchschalten nicht traege
// wirkt, lang genug um schnelles Blaettern zusammenzufassen.
const PREVIEW_DELAY_MS = 300;

export default {
  mixins: [keyHandler],
  components: { BackButton },
  data() {
    return {
      focusChannelIndex: 0,
      focusProgramIndex: 0,
      // Liegt der Fokus auf dem Zurueck-Knopf statt im Raster?
      isBackFocused: false,
      // Erste sichtbare Kanalzeile (Scrollposition der Kanalliste).
      rowOffset: 0,
      // Beginn des sichtbaren Zeitfensters.
      viewStartMs: Date.now(),
      nowMs: Date.now(),
      showInfo: false,
      livePreviewEnabled: LIVE_PREVIEW_ENABLED,
      nowTimer: null,
      refreshTimer: null,
      previewTimer: null,
      // true, sobald die Videoflaeche verkleinert wurde. Steuert, ob
      // stopPreview() ueberhaupt etwas zurueckzusetzen hat.
      previewActive: false,
      // Scrollzustand des Beschreibungstexts im Info-Fenster.
      infoScrollable: false,
      infoAtStart: true,
      infoAtEnd: true,
      // Fokuszone in der Info-Ansicht: 'list' = Kanalliste (HOCH/RUNTER schaltet
      // den Sender), 'text' = Beschreibung (HOCH/RUNTER blaettert). LINKS/RECHTS
      // wechselt die Zone. Damit haengt die Bedeutung von HOCH/RUNTER nicht mehr
      // an der Textlaenge, sondern ist vom Gast bestimmt.
      infoZone: 'text',
    };
  },
  computed: {
    channels() {
      return this.$store.getters['epgGuide2/getChannels'];
    },
    epgWindow() {
      return this.$store.getters['epgGuide2/getWindow'];
    },
    isLoading() {
      return this.$store.getters['epgGuide2/isLoading'];
    },
    progress() {
      return this.$store.getters['epgGuide2/getProgress'];
    },
    error() {
      return this.$store.getters['epgGuide2/getError'];
    },
    view() {
      return viewWindow(this.viewStartMs, VIEW_MINUTES);
    },
    slots() {
      return timeSlots(this.view);
    },
    nowPercent() {
      return nowMarkerPercent(this.view, this.nowMs);
    },
    dayLabel() {
      return formatDayShort(new Date(this.viewStartMs));
    },
    clockLabel() {
      return formatTime(new Date(this.nowMs));
    },
    viewRangeLabel() {
      return formatTime(new Date(this.view.start)) + ' – ' + formatTime(new Date(this.view.end));
    },
    focusChannel() {
      return this.channels[this.focusChannelIndex] || null;
    },
    focusChannelName() {
      return this.focusChannel ? this.focusChannel.name : '';
    },
    focusLogo() {
      return this.logoFor(this.focusChannel);
    },
    focusPrograms() {
      if (!this.focusChannel) return [];
      return this.$store.getters['epgGuide2/getPrograms'](this.focusChannel.epgChannelId);
    },
    focusProgram() {
      return this.focusPrograms[this.focusProgramIndex] || null;
    },
    focusProgress() {
      return programProgress(this.focusProgram, this.nowMs);
    },
    // Die Sendung, die auf dem fokussierten Sender JETZT laeuft - unabhaengig
    // davon, welche Sendung in der Zeitleiste ausgewaehlt ist.
    nowProgram() {
      const programs = this.focusPrograms;
      for (let i = 0; i < programs.length; i++) {
        if (isRunning(programs[i], this.nowMs)) return programs[i];
      }
      return null;
    },
    // true, wenn die Beschreibung oben eine andere Sendung zeigt als die, die im
    // Vorschaubild laeuft. Nur dann wird die Zusatzangabe eingeblendet.
    nowProgramDiffers() {
      const now = this.nowProgram;
      if (!now || !this.focusProgram) return false;
      return now.id !== this.focusProgram.id;
    },
    nowProgramProgress() {
      return programProgress(this.nowProgram, this.nowMs);
    },
    focusDescription() {
      const p = this.focusProgram;
      if (!p) return '';
      // description ist der ausfuehrliche Text, text die Kurzfassung.
      return p.description || p.text || this.getNoDescription;
    },
    // Nur die sichtbaren Zeilen, jeweils nur die Sendungen im Sichtfenster.
    visibleRows() {
      const rows = [];
      const end = Math.min(this.channels.length, this.rowOffset + ROWS);
      for (let i = this.rowOffset; i < end; i++) {
        const channel = this.channels[i];
        if (!channel) continue;
        const programs = this.$store.getters['epgGuide2/getPrograms'](channel.epgChannelId);
        const items = [];
        for (let k = 0; k < programs.length; k++) {
          const layout = programLayout(programs[k], this.view);
          if (!layout) continue;
          items.push({
            program: programs[k],
            index: k,
            layout: layout,
            progress: programProgress(programs[k], this.nowMs),
          });
        }
        rows.push({
          channel: channel,
          index: i,
          logo: this.logoFor(channel),
          programs: items,
          status: this.$store.getters['epgGuide2/getChannelStatus'](channel.epgChannelId),
        });
      }
      return rows;
    },
    // Fuellt die Zeilenflaeche auf ROWS Zeilen auf, damit eine Kanalliste mit
    // weniger als ROWS Sendern nicht auf Zeilen in Schirmhoehe aufgeblasen wird.
    rowFillerGrow() {
      return Math.max(0, ROWS - this.visibleRows.length);
    },
    // Theme ueber CSS-Variablen. Neutral und unauffaellig als Default; sobald der
    // Customizer eigene EPG-Werte liefert, koennen sie hier eingehaengt werden,
    // ohne das Stylesheet zu aendern.
    themeStyles() {
      const c = (this.$store.state.customizer && this.$store.state.customizer.epgGuide) || {};
      const style = {};
      if (c.background) style['--epg2-bg'] = c.background;
      if (c.surface) style['--epg2-surface'] = c.surface;
      if (c.text) style['--epg2-text'] = c.text;
      if (c.muted) style['--epg2-muted'] = c.muted;
      if (c.focus) style['--epg2-focus'] = c.focus;
      if (c.accent) style['--epg2-accent'] = c.accent;
      return style;
    },
    getTitle() {
      return this.$i18n('epg.title');
    },
    getBack() {
      return this.$i18n('back.name');
    },
    getLoading() {
      return this.$i18n('epg.loading');
    },
    getNoData() {
      return this.$i18n('epg.noData');
    },
    getNoDescription() {
      return this.$i18n('epg.noDescription');
    },
    getRunning() {
      return this.$i18n('epg.running');
    },
    getHint() {
      return this.$i18n('epg.hint');
    },
    getInfoHint() {
      return this.$i18n('epg.infoHint');
    },
    getNowOnAir() {
      return this.$i18n('epg.nowOnAir');
    },
  },
  methods: {
    logoFor(channel) {
      if (!channel) return EPG_LOGO_FALLBACK;
      // Dieselbe Quelle wie die TV-Kanalliste: der EPG-Datensatz im tv-Store.
      const epg = this.$store.getters['tv/getEpgById'](channel.epgChannelId);
      return epgLogoUrl(epg);
    },
    onLogoError(event) {
      // Fehlendes Logo nicht als kaputtes Bild stehen lassen. Guard gegen eine
      // Endlosschleife, falls auch das Fallback-Bild fehlt.
      const img = event && event.target;
      if (img && img.src !== EPG_LOGO_FALLBACK) {
        img.setAttribute('src', EPG_LOGO_FALLBACK);
      }
    },
    // Haelt die fokussierte Zeile im sichtbaren Bereich.
    syncRowOffset() {
      if (this.focusChannelIndex < this.rowOffset) {
        this.rowOffset = this.focusChannelIndex;
      } else if (this.focusChannelIndex >= this.rowOffset + ROWS) {
        this.rowOffset = this.focusChannelIndex - ROWS + 1;
      }
      const maxOffset = Math.max(0, this.channels.length - ROWS);
      if (this.rowOffset > maxOffset) this.rowOffset = maxOffset;
      if (this.rowOffset < 0) this.rowOffset = 0;
    },
    // Zeitachse so verschieben, dass die fokussierte Sendung sichtbar ist.
    syncViewToFocus() {
      if (!this.epgWindow || !this.focusProgram) return;
      this.viewStartMs = viewStartFor(this.focusProgram, this.viewStartMs, this.epgWindow);
    },
    // Beim Kanalwechsel die Sendung waehlen, die zur bisherigen Fokuszeit laeuft.
    // Ein Sprung auf Index 0 wuerde die Zeitachse jedes Mal zurueckwerfen.
    moveChannel(step) {
      const count = this.channels.length;
      if (!count) return;
      // Ringfoermig: hinter der letzten Zeile geht es wieder bei der ersten
      // weiter und umgekehrt. Bei 60+ Sendern spart das den Weg durch die
      // ganze Liste, nur um den Anfang zu erreichen.
      //
      // Zweimal Modulo, weil % in JavaScript das Vorzeichen des Zaehlers
      // behaelt: (-1) % 60 ist -1, nicht 59.
      const next = (((this.focusChannelIndex + step) % count) + count) % count;
      // Bei genau EINEM Sender kommt der Ring auf derselben Zeile heraus. Das
      // muss ein Nichts-passiert bleiben - changeInfoChannel erkennt daran,
      // dass es die Vorschau nicht neu anstossen braucht.
      if (next === this.focusChannelIndex) return;
      const anchor = this.focusProgram ? this.focusProgram.startMs : this.view.start;
      this.focusChannelIndex = next;
      const programs = this.focusPrograms;
      this.focusProgramIndex = programs.length ? indexOfCurrentProgram(programs, anchor) : 0;
      this.syncRowOffset();
      this.syncViewToFocus();
    },
    moveProgram(step) {
      const programs = this.focusPrograms;
      if (!programs.length) {
        // Kein EPG fuer diesen Sender: dann nur die Zeitachse bewegen, damit der
        // Gast nicht in einer leeren Zeile festhaengt.
        this.shiftView(step);
        return;
      }
      const next = this.focusProgramIndex + step;
      if (next < 0 || next >= programs.length) {
        this.shiftView(step);
        return;
      }
      this.focusProgramIndex = next;
      this.syncViewToFocus();
    },
    shiftView(step) {
      if (!this.epgWindow) return;
      this.viewStartMs = clampViewStart(
        this.viewStartMs + step * VIEW_MINUTES * MINUTE,
        this.epgWindow
      );
    },
    pageChannels(step) {
      const count = this.channels.length;
      if (!count) return;
      const last = count - 1;
      let next = this.focusChannelIndex + step * ROWS;
      // Ueber den Rand hinaus erst auf die letzte bzw. erste Zeile, und nur
      // von dort aus auf die andere Seite. Direkt durchzuspringen wuerde die
      // letzten Zeilen ueberspringen - bei 60 Sendern und 7 Zeilen kaeme man
      // von Zeile 56 auf Zeile 3 und saehe 57 bis 59 nie.
      if (next > last) next = this.focusChannelIndex === last ? 0 : last;
      else if (next < 0) next = this.focusChannelIndex === 0 ? last : 0;
      this.focusChannelIndex = next;
      const programs = this.focusPrograms;
      this.focusProgramIndex = programs.length ? indexOfCurrentProgram(programs, this.nowMs) : 0;
      this.syncRowOffset();
      this.syncViewToFocus();
    },
    // Zweites OK: zum Sender springen und abspielen. Gleicher Weg wie im alten
    // TV-Guide, damit der TV-Player unveraendert bleibt.
    jumpToChannel() {
      const channel = this.focusChannel;
      if (!channel) return;
      // Vor dem Wechsel die Videoflaeche zurueckgeben, damit der TV-Player mit
      // Vollbild startet und nicht mit dem Vorschau-Rechteck.
      this.stopPreview();

      // WICHTIG: _id, nicht id. Die Kanaele aus fetchTvPackages tragen ihre
      // Kennung als _id, die Kanaele im tv-Store (fetchChannelList) als id.
      // tv/setEPGGuideStartChannel vergleicht channel.id === startChannel.id -
      // mit channel.id (hier undefined) findet die Suche nichts, bricht ab und
      // der TV-Player startet auf Kanal 1 bzw. dem letzten Sender.
      const channelId = channel._id || channel.id;
      if (!channelId) {
        console.log('[EPGGuide2] Kanal ohne Kennung, Sprung nicht moeglich:', channel);
        return;
      }
      console.log('[EPGGuide2] Sprung zu Kanal', channel.name, channelId);
      this.$store.commit(
        'tv/setEPGGuideStartChannel',
        { index: this.focusChannelIndex, id: channelId },
        { root: true }
      );
      this.$router.push({ name: 'TVPlayer', query: { origin: 'epg' } }).catch(() => {});
    },
    // ---- Info-Text scrollen -----------------------------------------------
    // Der Text wird nicht gekuerzt, sondern gescrollt: eine Programmbeschreibung
    // abzuschneiden nimmt dem Gast Information, die sonst nirgends steht.
    scrollInfoText(dir) {
      const el = this.$refs.infoText;
      if (!el) return;
      el.scrollTop += dir * Math.max(40, Math.round(el.clientHeight * 0.6));
      this.updateInfoScrollState();
    },
    updateInfoScrollState() {
      const el = this.$refs.infoText;
      if (!el) {
        this.infoScrollable = false;
        this.infoAtStart = true;
        this.infoAtEnd = true;
        return;
      }
      // 2px Toleranz: Zeilenhoehen ergeben selten exakt aufgehende Werte.
      this.infoScrollable = el.scrollHeight > el.clientHeight + 2;
      this.infoAtStart = el.scrollTop <= 2;
      this.infoAtEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    },
    // Kanalwechsel bei offener Info-Ansicht: Info neu aufbauen und die Vorschau
    // auf den neuen Sender umtunen (entprellt, siehe schedulePreview).
    changeInfoChannel(step) {
      const before = this.focusChannelIndex;
      this.moveChannel(step);
      if (this.focusChannelIndex === before) return; // Listenrand erreicht
      this.resetInfoScroll();
      this.schedulePreview();
    },
    // options.chooseZone: beim OEFFNEN der Info-Ansicht die Zone neu bestimmen.
    // Beim Senderwechsel darf das NICHT passieren - sonst wuerde der Gast aus der
    // Kanalliste herausgerissen, sobald ein Sender eine lange Beschreibung hat.
    resetInfoScroll(options) {
      const opts = options || {};
      const el = this.$refs.infoText;
      if (el) el.scrollTop = 0;
      // Nach dem Rendern messen - vorher stehen scrollHeight/clientHeight nicht.
      this.$nextTick(() => {
        this.updateInfoScrollState();
        if (opts.chooseZone) {
          this.infoZone = this.infoScrollable ? 'text' : 'list';
        } else if (this.infoZone === 'text' && !this.infoScrollable) {
          // Nach dem Wechsel gibt es hier nichts zu blaettern - zurueck in die
          // Liste, damit HOCH/RUNTER nicht wirkungslos wird.
          this.infoZone = 'list';
        }
      });
    },

    // ---- Live-Vorschau ----------------------------------------------------
    // Verzoegert starten, damit schnelles Blaettern im Info-Fenster nicht bei
    // jedem Tastendruck einen Kanalwechsel ausloest.
    schedulePreview() {
      this.cancelPreviewTimer();
      if (!this.livePreviewEnabled || !this.showInfo || !this.focusChannel) return;
      this.previewTimer = setTimeout(() => {
        this.previewTimer = null;
        if (!this.showInfo || !this.focusChannel) return;
        this.previewActive = true;
        TV.startPreview(this.focusChannel, PREVIEW_RECT);
      }, PREVIEW_DELAY_MS);
    },
    cancelPreviewTimer() {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
        this.previewTimer = null;
      }
    },
    // Muss an JEDEM Ausgang laufen: auf LG bleibt die verkleinerte Videoflaeche
    // sonst bestehen und der TV-Player startet mit falscher Groesse.
    stopPreview() {
      this.cancelPreviewTimer();
      if (!this.previewActive) return;
      this.previewActive = false;
      TV.stopPreview();
    },
    tickNow() {
      this.nowMs = Date.now();
    },
    tickRefresh() {
      // Der Store entscheidet, ob wirklich geladen wird (Fenster/Alter).
      this.$store.dispatch('epgGuide2/refresh').then((did) => {
        if (did) this.resetFocusToNow();
      }).catch(() => {});
    },
    resetFocusToNow() {
      if (!this.epgWindow) return;
      this.viewStartMs = clampViewStart(Date.now(), this.epgWindow);
      const programs = this.focusPrograms;
      this.focusProgramIndex = programs.length ? indexOfCurrentProgram(programs, Date.now()) : 0;
      this.syncViewToFocus();
    },
    eventHandler(key) {
      // Im Info-Overlay nur OK und Zurueck; die Navigation dahinter soll nicht
      // unbemerkt weiterlaufen.
      if (this.showInfo) {
        switch (key) {
          // LINKS/RECHTS wechselt die Fokuszone.
          //
          // Aus dem Text fuehren BEIDE Richtungen immer zurueck in die Liste -
          // die Textbox darf keine Sackgasse sein, aus der man nur mit ZURUECK
          // (und damit dem Schliessen der Info) herauskommt.
          // In den Text geht es nur, wenn dort ueberhaupt etwas zu blaettern ist.
          case KEYS.LEFT:
          case KEYS.RIGHT:
            if (this.infoZone === 'text') {
              this.infoZone = 'list';
            } else if (key === KEYS.RIGHT && this.infoScrollable) {
              this.infoZone = 'text';
            }
            break;
          // HOCH/RUNTER wirkt auf die aktive Zone.
          case KEYS.UP:
            if (this.infoZone === 'text') this.scrollInfoText(-1);
            else this.changeInfoChannel(-1);
            break;
          case KEYS.DOWN:
            if (this.infoZone === 'text') this.scrollInfoText(1);
            else this.changeInfoChannel(1);
            break;
          // Die Kanaltasten schalten immer den Sender - unabhaengig von der Zone.
          // Der Fokus wandert dabei in die Liste, damit HOCH/RUNTER danach
          // weiterschaltet und nicht ueberraschend den Text blaettert.
          case KEYS.CHANNEL_UP:
            this.infoZone = 'list';
            this.changeInfoChannel(-1);
            break;
          case KEYS.CHANNEL_DOWN:
            this.infoZone = 'list';
            this.changeInfoChannel(1);
            break;
          case KEYS.ENTER:
            this.jumpToChannel();
            break;
          case KEYS.BACK:
          case KEYS.GOBACK:
          case KEYS.EXIT:
            this.showInfo = false;
            this.stopPreview();
            break;
          default:
            break;
        }
        return;
      }

      // Fokus auf dem Zurueck-Knopf: nur OK loest ihn aus, jede andere
      // Richtung fuehrt zurueck ins Raster. Der Knopf darf keine Sackgasse
      // sein - dieselbe Regel wie bei der Textbox im Info-Fenster oben.
      if (this.isBackFocused) {
        switch (key) {
          case KEYS.ENTER:
            this.leave();
            return;
          case KEYS.BACK:
          case KEYS.GOBACK:
          case KEYS.EXIT:
            this.leave();
            return;
          case KEYS.DOWN:
          case KEYS.LEFT:
          case KEYS.RIGHT:
            this.isBackFocused = false;
            return;
          case KEYS.UP:
            // Ueber den Knopf hinaus nach oben geht es ans ENDE der Liste -
            // damit ist der Ring auch nach oben geschlossen und der Knopf
            // eine Station darin statt eine Sackgasse.
            //
            // moveChannel(-1) erledigt das von selbst: der Fokus steht noch
            // auf der ersten Zeile (nur von dort fuehrt HOCH auf den Knopf),
            // und ein Schritt nach oben ist im Ring die letzte Zeile. Ueber
            // denselben Weg stimmen auch Scrollposition und Zeitachse.
            //
            // Nur wenn es ueberhaupt Zeilen gibt: waehrend die Kanalliste
            // laedt, bliebe der Fokus sonst nirgends sichtbar - weder auf dem
            // Knopf noch im leeren Raster.
            if (!this.channels.length) return;
            this.isBackFocused = false;
            this.moveChannel(-1);
            return;
          default:
            // Alles Uebrige (Kanaltasten, GRUEN) wirkt wieder auf das Raster.
            this.isBackFocused = false;
            break;
        }
      }

      switch (key) {
        case KEYS.UP:
          // Aus der obersten Zeile nach oben auf den Zurueck-Knopf. Eine
          // andere freie Richtung gibt es im Raster nicht - HOCH/RUNTER
          // blaettert die Sender, LINKS/RECHTS die Sendungen.
          //
          // Der Knopf ist damit eine Station im Ring, aber nur auf dem Weg
          // nach OBEN:
          //   letzte Zeile -- RUNTER --> erste Zeile   (Knopf uebersprungen)
          //   erste Zeile  -- HOCH ---> Knopf -- HOCH --> letzte Zeile
          // Wer nur nach unten blaettert, kommt also nie auf ihn; wer nach
          // oben blaettert, kommt an ihm vorbei und danach ans Listenende.
          if (this.focusChannelIndex === 0) {
            this.isBackFocused = true;
            break;
          }
          this.moveChannel(-1);
          break;
        case KEYS.DOWN:
          this.moveChannel(1);
          break;
        case KEYS.LEFT:
          this.moveProgram(-1);
          break;
        case KEYS.RIGHT:
          this.moveProgram(1);
          break;
        case KEYS.CHANNEL_UP:
        case KEYS.PAGE_UP:
          this.pageChannels(-1);
          break;
        case KEYS.CHANNEL_DOWN:
        case KEYS.PAGE_DOWN:
          this.pageChannels(1);
          break;
        case KEYS.ENTER:
          if (this.focusProgram) {
            this.showInfo = true;
            // chooseZone: beim Oeffnen in den Text, wenn dort etwas zu blaettern
            // ist - sonst direkt in die Kanalliste.
            this.resetInfoScroll({ chooseZone: true });
            this.schedulePreview();
          }
          break;
        case KEYS.GREEN:
          // Zurueck zu "jetzt" - nach langem Blaettern der schnellste Weg.
          this.resetFocusToNow();
          break;
        case KEYS.BACK:
        case KEYS.GOBACK:
        case KEYS.EXIT:
          this.leave();
          break;
        default:
          break;
      }
    },
    /**
     * Seite verlassen.
     *
     * stopPreview() MUSS dabei laufen: auf LG ist die verkleinerte
     * Videoflaeche globaler Geraetezustand und bliebe sonst fuer den
     * TV-Player bestehen (siehe PREVIEW_RECT weiter oben).
     */
    leave() {
      this.stopPreview();
      this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
    },
  },
  mounted() {
    this.nowTimer = setInterval(this.tickNow, NOW_TICK_MS);
    this.refreshTimer = setInterval(this.tickRefresh, REFRESH_TICK_MS);
    // tv/prepare liefert die Logo-Metadaten UND die Kanalliste, auf der
    // tv/setEPGGuideStartChannel den Sender sucht - ohne sie bliebe der Sprung
    // zum Sender wirkungslos. navigation/init stoesst das zwar an, aber ohne zu
    // warten; hier nochmal (die Aktion ist durch epgLoaded idempotent).
    this.$store.dispatch('tv/prepare').catch((err) => {
      console.log('[EPGGuide2] tv/prepare fehlgeschlagen:', err);
    });
    // Geht die App in den Hintergrund (Standby, fremder App-Start), die Vorschau
    // beenden - sonst bleibt auf LG die verkleinerte Videoflaeche stehen.
    if (typeof document !== 'undefined') {
      this._onVisibility = () => {
        if (document.hidden) this.stopPreview();
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }
    // init laeuft asynchron und laedt zuerst nur die sichtbaren Kanaele; die
    // Seite ist deshalb sofort bedienbar.
    this.$store.dispatch('epgGuide2/init').then(() => {
      this.resetFocusToNow();
    }).catch((err) => {
      console.log('[EPGGuide2] init fehlgeschlagen:', err);
    });
  },
  beforeDestroy() {
    if (this.nowTimer) clearInterval(this.nowTimer);
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.nowTimer = null;
    this.refreshTimer = null;
    // Letzte Sicherung: egal wie die Seite verlassen wird (Zurueck, Sprung zum
    // Sender, Socket-Navigation von aussen), die Videoflaeche geht auf Vollbild
    // zurueck. Ohne das startet der TV-Player auf LG mit dem Vorschau-Rechteck.
    this.stopPreview();
    if (this._onVisibility && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._onVisibility = null;
    }
  },
};
</script>

<style scoped>
/* Neutrale, zurueckhaltende Grundfarben. Alle Werte als CSS-Variablen, damit sie
   spaeter aus dem Customizer ueberschrieben werden koennen (siehe themeStyles);
   die Regeln unten greifen ausschliesslich auf die Variablen zu. */
.epg2 {
  --epg2-bg: #1c1f22;
  --epg2-surface: #262a2e;
  --epg2-surface-alt: #2e3338;
  --epg2-text: #e8eaec;
  --epg2-muted: #9aa1a8;
  --epg2-focus: #f0f2f4;
  --epg2-accent: #7f8c96;
  --epg2-line: #3a4046;

  background-color: var(--epg2-bg);
  box-sizing: border-box;
  color: var(--epg2-text);
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  padding: 2vh 2vw;
  width: 100vw;
}

/* Waehrend der Live-Vorschau MUSS der Seitenhintergrund transparent sein.
   Das Videobild liegt auf einer eigenen Hardware-Ebene HINTER dem HTML (auf
   Tizen zOrder 'BEHIND', auf LG genauso) - ein opaker Hintergrund verdeckt es
   vollstaendig. Genau deshalb war zunaechst nur der Ton zu hoeren.
   Der TV-Player macht es an seiner Videoflaeche identisch
   (.tv-player__video { background: transparent }). */
.epg2--preview {
  background-color: transparent;
}

/* Waehrend der Vorschau nur die PROGRAMMSPUREN ausblenden, nicht die ganze
   Zeile: die Sendungskacheln (.epg2__program) sind deckend und liegen ueber dem
   Vorschau-Rechteck - das Bild war deshalb nur in den Luecken zwischen den
   Zeilen zu sehen. Die Kanalspalte links (bis 20vw) liegt ausserhalb des
   Rechtecks (ab 55vw) und bleibt sichtbar, damit weiter umgeschaltet werden kann.
   visibility statt display, damit Layout und Scrollposition erhalten bleiben und
   beim Schliessen kein Reflow entsteht. */
.epg2--preview .epg2__lane {
  visibility: hidden;
}
/* Die Trennlinie gehoert zur ZEILE und spannt damit ueber die volle Breite -
   auch ueber das Vorschau-Rechteck. visibility:hidden auf der Lane entfernt sie
   nicht, sie lag als schmaler grauer Strich im Videobild.
   Waehrend der Vorschau wandert sie deshalb in die Kanalspalte (endet bei 20vw)
   und bleibt dort als Orientierung erhalten. */
.epg2--preview .epg2__row {
  border-bottom-color: transparent;
}
.epg2--preview .epg2__channel {
  border-bottom: 1px solid var(--epg2-line);
  box-sizing: border-box;
}
/* Fokus in der Kanalspalte markieren - ohne die Programmspuren ist die fette
   Schrift allein zu wenig Orientierung. */
.epg2--preview .epg2__row--focus .epg2__channel {
  background-color: var(--epg2-surface-alt);
  border-radius: 0.4vh;
}

/* Aktive Fokuszone sichtbar machen: liegt der Fokus in der Kanalliste, bekommt
   die Zeile zusaetzlich einen Balken; liegt er im Text, wird der Rahmen des
   Info-Kastens hervorgehoben. Sonst waere nicht erkennbar, worauf HOCH/RUNTER
   gerade wirkt. */
.epg2--zone-list .epg2__row--focus .epg2__channel {
  background-color: var(--epg2-surface-alt);
  border-radius: 0.4vh;
  box-shadow: inset 0.3vw 0 0 var(--epg2-focus);
}
.epg2__info-box--active {
  border-color: var(--epg2-focus);
}

/* Laufende Sendung unter dem Vorschaubild.
   Beginnt unterhalb des Rechtecks (30vh + 35vh) plus Rahmenring, damit sie das
   Bild nicht beschneidet, und endet auf derselben Linie wie der Info-Kasten
   (25vh + 50vh = 75vh) - die rechte und die linke Spalte schliessen damit
   buendig ab. Der deckende Hintergrund ist unkritisch, weil die Flaeche
   ausserhalb von PREVIEW_RECT liegt. */
.epg2__preview-now {
  background-color: var(--epg2-surface);
  border: 1px solid var(--epg2-line);
  border-radius: 0.8vh;
  box-sizing: border-box;
  height: 8.5vh;
  left: 55vw;
  overflow: hidden;
  padding: 0.8vh 1.2vw;
  position: absolute;
  top: 66.5vh;
  width: 35vw;
}
.epg2__preview-now-flag {
  color: var(--epg2-accent);
}
.epg2__preview-now-label {
  color: var(--epg2-accent);
  font-size: 1.6vh;
  letter-spacing: 0.1vw;
  text-transform: uppercase;
}
.epg2__preview-now-title {
  font-size: 2vh;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.epg2__preview-now-time {
  color: var(--epg2-muted);
  font-size: 1.7vh;
}
.epg2__preview-now-bar {
  background-color: var(--epg2-surface-alt);
  height: 0.4vh;
  margin-top: 0.7vh;
  width: 100%;
}
.epg2__preview-now-bar-fill {
  background-color: var(--epg2-accent);
  height: 100%;
}

/* Abstaende hier und in den folgenden Flex-Zeilen ueber margin statt ueber gap:
   gap in Flex-Containern kennt Chrome erst ab 84, auf webOS 5 (Chrome 68) wird
   die Angabe stillschweigend ignoriert und die Elemente kleben aneinander. */
.epg2__head {
  align-items: baseline;
  display: flex;
  flex: 0 0 auto;
  margin-bottom: 1.5vh;
}
/* Zurueck-Knopf in die Kopfzeile einpassen.
   Die Vorgaben der Komponente (schwarz, 24px, 120px breit) sind fuer die
   grossen Kacheln der Startseite gemacht und wuerden diese kompakte Zeile
   sprengen. Selektor bewusst zweistufig - er muss die scoped Regeln von
   BackButton.vue schlagen, die dieselbe Gewichtung haetten.
   align-self, weil die Kopfzeile an der Schriftlinie ausrichtet: ein Knopf mit
   Rahmen und Innenabstand haengt daran sonst schief. */
.epg2__head .epg2__back {
  align-self: center;
  background-color: var(--epg2-surface);
  border-color: var(--epg2-line);
  color: var(--epg2-text);
  font-size: 2.1vh;
  margin-right: 2vw;
  min-width: 0;
  padding: 0.7vh 1.6vh;
}
/* Fokus mit denselben Mitteln wie im Raster (heller Rahmen), damit der Gast
   ihn als dieselbe Art Auswahl liest. */
.epg2__head .epg2__back.back-button--active {
  background-color: var(--epg2-surface-alt);
  border-color: var(--epg2-focus);
}

.epg2__title {
  font-size: 3vh;
  font-weight: 700;
  margin-right: 2vw;
}
.epg2__clock {
  font-size: 2.6vh;
}
.epg2__status {
  color: var(--epg2-muted);
  font-size: 2vh;
  margin-left: auto;
}
.epg2__error {
  color: #d99;
}

/* Breite der Kanalspalte an einer Stelle, damit Achse und Zeilen bündig sind. */
.epg2__axis,
.epg2__row {
  display: flex;
  align-items: stretch;
}
.epg2__axis-spacer,
.epg2__channel {
  flex: 0 0 18vw;
  box-sizing: border-box;
  padding-right: 0.8vw;
}
.epg2__axis-lane,
.epg2__lane {
  flex: 1 1 auto;
  position: relative;
  min-width: 0;
}

.epg2__axis {
  border-bottom: 1px solid var(--epg2-line);
  flex: 0 0 auto;
  height: 4vh;
}
.epg2__axis-spacer {
  color: var(--epg2-muted);
  font-size: 2vh;
}
.epg2__axis-slot {
  border-left: 1px solid var(--epg2-line);
  box-sizing: border-box;
  color: var(--epg2-muted);
  font-size: 1.9vh;
  padding-left: 0.4vw;
  position: absolute;
  top: 0;
}

/* "Jetzt"-Markierung: dünne Linie über Achse und Zeilen. */
.epg2__now {
  background-color: var(--epg2-accent);
  bottom: 0;
  position: absolute;
  top: 0;
  width: 2px;
}
.epg2__now--row {
  opacity: 0.5;
}

/* Zeilenhoehe per Flex-Verteilung, NICHT per Prozenthoehe.
   .epg2__rows ist ein Flex-Item, dessen Hoehe erst aus flex-grow entsteht. Ein
   height: calc(100% / 7) auf den Zeilen setzt voraus, dass der Browser diese
   Hoehe als bestimmt behandelt - das tun erst neuere Chrome-Versionen. Auf
   webOS 5 (Chrome 68) loest die Prozentangabe zu auto auf und die Zeilen fallen
   auf ihre Inhaltshoehe zusammen. flex: 1 1 0px braucht keine bestimmte
   Elternhoehe und funktioniert auf allen Zielgeraeten. */
.epg2__rows {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
.epg2__row {
  border-bottom: 1px solid var(--epg2-line);
  flex: 1 1 0px;
  min-height: 0;
}
/* Haelt die Zeilen auch dann auf 1/ROWS, wenn weniger Sender vorhanden sind -
   sonst zieht die Flex-Verteilung z.B. vier Sender auf den ganzen Schirm. */
.epg2__rows-filler {
  flex-shrink: 1;
  flex-basis: 0px;
  min-height: 0;
}
.epg2__row--focus .epg2__channel-name {
  color: var(--epg2-focus);
  font-weight: 700;
}

.epg2__channel {
  align-items: center;
  display: flex;
  overflow: hidden;
}
.epg2__channel-pos {
  color: var(--epg2-muted);
  flex: 0 0 2.5vw;
  font-size: 2vh;
  margin-right: 0.6vw;
  text-align: right;
}
.epg2__channel-logo {
  background-color: #fff;
  flex: 0 0 auto;
  height: 3.2vh;
  margin-right: 0.6vw;
  object-fit: contain;
  width: 3.2vw;
}
.epg2__channel-name {
  font-size: 2vh;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.epg2__program {
  background-color: var(--epg2-surface);
  border: 1px solid var(--epg2-line);
  border-radius: 0.4vh;
  bottom: 0.4vh;
  box-sizing: border-box;
  overflow: hidden;
  padding: 0.3vh 0.5vw;
  position: absolute;
  top: 0.4vh;
}
.epg2__program--running {
  background-color: var(--epg2-surface-alt);
}
.epg2__program--focus {
  background-color: var(--epg2-surface-alt);
  border-color: var(--epg2-focus);
  border-width: 0.3vh;
}
.epg2__program-title {
  font-size: 1.9vh;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.epg2__program-time {
  color: var(--epg2-muted);
  font-size: 1.6vh;
}
/* Schmaler Fortschrittsbalken am unteren Rand der laufenden Sendung. */
.epg2__program-progress {
  background-color: var(--epg2-accent);
  bottom: 0;
  height: 0.4vh;
  left: 0;
  position: absolute;
}
.epg2__empty {
  color: var(--epg2-muted);
  font-size: 1.8vh;
  left: 0.5vw;
  position: absolute;
  top: 30%;
}

.epg2__hint {
  color: var(--epg2-muted);
  flex: 0 0 auto;
  font-size: 1.8vh;
  padding-top: 1vh;
}

/* Kein deckender Scrim ueber dem ganzen Bild: die Videoebene liegt hinter dem
   HTML, ein Scrim wuerde die Vorschau verdecken. Stattdessen liegt der deckende
   Info-Kasten links, die Vorschau rechts daneben in einem freien Bereich. */
.epg2__info {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}
/* Flex-Spalte: Kopf und Hilfstext haben feste Hoehe, nur der Beschreibungstext
   dehnt sich und scrollt. So bleibt der Hilfstext IMMER sichtbar, egal wie lang
   die Beschreibung ist. */
.epg2__info-box {
  background-color: var(--epg2-surface);
  border: 1px solid var(--epg2-line);
  border-radius: 0.8vh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 50vh;
  /* Beginnt rechts der Kanalspalte (2vw Padding + 18vw Spalte = 20vw) und endet
     vor dem Vorschau-Rechteck (ab 55vw). So bleibt die Kanalliste immer
     sichtbar und bedienbar. */
  left: 21vw;
  overflow: hidden;
  padding: 2.5vh 1.8vw;
  position: absolute;
  top: 25vh;
  width: 32vw;
}
.epg2__info-channel,
.epg2__info-title,
.epg2__info-time,
.epg2__info-progress,
.epg2__info-more,
.epg2__info-hint {
  flex: 0 0 auto;
}
.epg2__info-more {
  color: var(--epg2-muted);
  font-size: 1.8vh;
  letter-spacing: 0.4vw;
  text-align: right;
}
/* Muss deckungsgleich mit PREVIEW_RECT im Script sein.
   35vw/35vh ergibt auf einem 16:9-Panel ein 16:9-Rechteck (1920*0.35=672,
   672*9/16=378, 378/1080=35%). */
.epg2__preview {
  background-color: transparent;
  /* Rahmen als box-shadow-Ring, NICHT als border: ein border wuerde bei
     box-sizing:border-box in die Flaeche hineinragen und das Videobild
     beschneiden, weil PREVIEW_RECT der Aussenkante entspricht. Der Ring liegt
     komplett aussen, die Bildflaeche bleibt exakt 35vw x 35vh.
     Farbe und Radius wie bei den uebrigen Kaesten (--epg2-line, 0.8vh).
     Der Ring folgt dem border-radius und deckt damit die eckigen Ecken des
     Videobilds ab - die Videoebene selbst kann keine runden Ecken. */
  border-radius: 0.8vh;
  box-shadow:
    0 0 0 0.8vh var(--epg2-line),
    0 1vh 3vh rgba(0, 0, 0, 0.55);
  height: 35vh;
  left: 55vw;
  position: absolute;
  top: 30vh;
  width: 35vw;
}
.epg2__info-channel {
  align-items: center;
  color: var(--epg2-muted);
  display: flex;
  font-size: 2vh;
  margin-bottom: 1vh;
}
.epg2__info-channel .epg2__channel-logo {
  margin-right: 0.8vw;
}
.epg2__info-title {
  font-size: 3.2vh;
  font-weight: 700;
  margin-bottom: 0.8vh;
}
.epg2__info-time {
  font-size: 2.2vh;
  margin-bottom: 1vh;
}
.epg2__info-live {
  color: var(--epg2-accent);
  margin-left: 1vw;
}
.epg2__info-progress {
  background-color: var(--epg2-surface-alt);
  height: 0.5vh;
  margin-bottom: 1.5vh;
  width: 100%;
}
.epg2__info-progress-bar {
  background-color: var(--epg2-accent);
  height: 100%;
}
/* Nimmt den restlichen Platz und scrollt (HOCH/RUNTER). overflow-y: auto statt
   hidden, damit scrollTop ueberhaupt wirkt; eine Scrollbar zeigen TV-Browser
   hier nicht an. */
.epg2__info-text {
  flex: 1 1 auto;
  font-size: 2vh;
  line-height: 1.4;
  min-height: 0;
  overflow-y: auto;
}
.epg2__info-hint {
  color: var(--epg2-muted);
  font-size: 1.8vh;
  padding-top: 2vh;
}
</style>



// WEBPACK FOOTER //
// src/pages/EPGGuide2.vue